import React, { useState, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Divider,
  Grid,
  InputAdornment,
  Alert,
  Chip,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import LinkIcon from '@mui/icons-material/Link';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import { toLocalISO } from 'routes/Pages/GenTask/components/SharedFormFields';

const DRAWER_WIDTH = 480;

// ─── Google Maps link parser ───────────────────────────────────────────────────
// Handles formats:
//   https://www.google.com/maps/place/.../@LAT,LNG,ZOOMz/...
//   https://maps.google.com/?q=LAT,LNG
//   https://goo.gl/maps/... (short links — can't resolve without fetch, so we inform user)
//   https://www.google.com/maps/@LAT,LNG,ZOOMz
function parseGoogleMapsLink(url) {
  if (!url) return null;

  try {
    // Format: /@lat,lng,zoom  (most common share link)
    const atMatch = url.match(/@(-?\d+\.\d+),(-?\d+\.\d+)/);
    if (atMatch) {
      return { lat: parseFloat(atMatch[1]), lng: parseFloat(atMatch[2]) };
    }

    // Format: ?q=lat,lng  or  ?ll=lat,lng
    const qMatch = url.match(/[?&](?:q|ll)=(-?\d+\.\d+),(-?\d+\.\d+)/);
    if (qMatch) {
      return { lat: parseFloat(qMatch[1]), lng: parseFloat(qMatch[2]) };
    }

    // Format: /place/.../data=...!8m2!3d<lat>!4d<lng>
    const dataMatch = url.match(/!3d(-?\d+\.\d+)!4d(-?\d+\.\d+)/);
    if (dataMatch) {
      return { lat: parseFloat(dataMatch[1]), lng: parseFloat(dataMatch[2]) };
    }

    // Format: maps/search/lat,lng
    const searchMatch = url.match(/search\/(-?\d+\.\d+),(-?\d+\.\d+)/);
    if (searchMatch) {
      return { lat: parseFloat(searchMatch[1]), lng: parseFloat(searchMatch[2]) };
    }

    return null;
  } catch {
    return null;
  }
}

// ─── Location Field ─────────────────────────────────────────────────────────
function LocationField({ value, onChange }) {
  const [mapLink, setMapLink] = useState('');
  const [parseStatus, setParseStatus] = useState(null); // null | 'success' | 'error'
  const [isParsing, setIsParsing] = useState(false);

  const handleLinkChange = e => {
    const link = e.target.value;
    setMapLink(link);
    setParseStatus(null);

    if (!link.trim()) {
      onChange({ lat: '', lng: '' });
      return;
    }

    setIsParsing(true);
    // Small delay for UX feel
    setTimeout(() => {
      const result = parseGoogleMapsLink(link);
      setIsParsing(false);
      if (result) {
        setParseStatus('success');
        onChange({ lat: result.lat, lng: result.lng });
      } else {
        setParseStatus('error');
        onChange({ lat: '', lng: '' });
      }
    }, 400);
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setMapLink(text);
      setParseStatus(null);
      setIsParsing(true);
      setTimeout(() => {
        const result = parseGoogleMapsLink(text);
        setIsParsing(false);
        if (result) {
          setParseStatus('success');
          onChange({ lat: result.lat, lng: result.lng });
        } else {
          setParseStatus('error');
          onChange({ lat: '', lng: '' });
        }
      }, 400);
    } catch {
      // clipboard access denied — user can paste manually
    }
  };

  return (
    <Box>
      <TextField
        fullWidth
        size="small"
        label="Google Maps Link"
        placeholder="Paste a Google Maps share link…"
        value={mapLink}
        onChange={handleLinkChange}
        required
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LinkIcon fontSize="small" color="action" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {isParsing ? (
                <CircularProgress size={16} />
              ) : parseStatus === 'success' ? (
                <CheckCircleIcon fontSize="small" color="success" />
              ) : parseStatus === 'error' ? (
                <ErrorOutlineIcon fontSize="small" color="error" />
              ) : (
                <IconButton size="small" onClick={handlePaste} title="Paste from clipboard">
                  <ContentPasteIcon fontSize="small" />
                </IconButton>
              )}
            </InputAdornment>
          ),
        }}
      />

      {/* Helper text */}
      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
        In Google Maps → right-click a location → "Share or embed map" → copy link
      </Typography>

      {/* Error state */}
      {parseStatus === 'error' && (
        <Alert severity="warning" sx={{ mt: 1, py: 0.5 }} icon={<ErrorOutlineIcon fontSize="small" />}>
          Couldn't extract coordinates. Try a full Google Maps URL (not a shortened goo.gl link).
        </Alert>
      )}

      {/* Success: show extracted coords as read-only chips */}
      {parseStatus === 'success' && value.lat && value.lng && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Chip
            icon={<LocationOnIcon />}
            label={`Lat: ${parseFloat(value.lat).toFixed(5)}`}
            size="small"
            color="success"
            variant="outlined"
          />
          <Chip
            icon={<LocationOnIcon />}
            label={`Lng: ${parseFloat(value.lng).toFixed(5)}`}
            size="small"
            color="success"
            variant="outlined"
          />
        </Box>
      )}
    </Box>
  );
}

// ─── Section Header ──────────────────────────────────────────────────────────
function SectionLabel({ children }) {
  return (
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        color: 'primary.main',
        mb: 1.5,
        mt: 0.5,
      }}>
      {children}
    </Typography>
  );
}

// ─── Main Drawer Component ───────────────────────────────────────────────────
const INITIAL_FORM = {
  // Contact
  crmContactFirst: '',
  crmContactEmail: '',
  crmContactPhone: '',
  crmLeadReferralSource: '',
  // Project
  crmProjectType: '',
  fellingLicenseNo: '',
  leadQuality: '',
  followUpDate: toLocalISO(),
  crmProjectDescription: '',
  // Location
  dropPin: '',
  dropPinLatitude: '',
  dropPinLongitude: '',
  // Address (auto-filled when coords resolve, or manual)
  crmAddressLine1: '',
  crmAddressCity: '',
  crmAddressTown: '',
  crmAddressZip: '',
  crmAddressCountry: '',
};

export function AddOwnerDrawer({ open, onClose, onSave }) {
  const [form, setForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(false);

  const set = field => e => setForm(prev => ({ ...prev, [field]: e.target.value }));

  const handleLocationChange = useCallback(({ lat, lng }) => {
    setForm(prev => ({
      ...prev,
      dropPinLatitude: lat,
      dropPinLongitude: lng,
    }));
  }, []);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // Build payload — hidden system fields are added here, not exposed to UI
      const payload = {
        crmTeamId: 1,
        crmStageId: 1,
        crmSalesRep: 1,
        crmDateOfInitialContact: toLocalISO(),
        ...form,
      };
      await onSave?.(payload);
      setForm(INITIAL_FORM);
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const isValid =
    form.crmContactFirst.trim() &&
    form.crmContactPhone.trim() &&
    form.followUpDate &&
    form.fellingLicenseNo &&
    form.dropPinLatitude &&
    form.dropPinLongitude;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: DRAWER_WIDTH,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
      sx={{ zIndex: 99999 }}>
      {/* ── Header ── */}
      <Box
        sx={{
          px: 3,
          py: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid',
          borderColor: 'divider',
          flexShrink: 0,
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PersonAddIcon color="primary" />
          <Typography variant="h6" fontWeight={600}>
            Add Owner
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      {/* ── Scrollable Body ── */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2.5 }}>
        {/* Contact Details */}
        <SectionLabel>Contact Details</SectionLabel>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Name *"
              placeholder="Enter name"
              value={form.crmContactFirst}
              onChange={set('crmContactFirst')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Email"
              placeholder="Enter email"
              type="email"
              value={form.crmContactEmail}
              onChange={set('crmContactEmail')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Mobile Number *"
              placeholder="+353..."
              value={form.crmContactPhone}
              onChange={e => {
                // Auto-prefix logic (mirrored from mobile)
                let clean = e.target.value.replace(/\D/g, '');
                let formatted = e.target.value;
                if (clean.startsWith('353')) {
                  formatted = '+' + clean;
                } else if (clean.startsWith('0')) {
                  formatted = '+353' + clean.substring(1);
                }
                setForm(prev => ({ ...prev, crmContactPhone: formatted }));
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Lead Source"
              placeholder="e.g. Marketing / Event"
              value={form.crmLeadReferralSource}
              onChange={set('crmLeadReferralSource')}
            />
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2.5 }} />

        {/* Enquiry Details */}
        <SectionLabel>Enquiry Details</SectionLabel>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Enquiry Type"
              placeholder="Select type"
              value={form.crmProjectType}
              onChange={set('crmProjectType')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Quality of Lead"
              placeholder="e.g. Hot / Warm / Cold"
              value={form.leadQuality}
              onChange={set('leadQuality')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Felling License No."
              placeholder="Enter license no."
              value={form.fellingLicenseNo}
              onChange={set('fellingLicenseNo')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Follow Up Date *"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={form.followUpDate}
              onChange={set('followUpDate')}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Notes"
              placeholder="Additional notes…"
              multiline
              rows={3}
              value={form.crmProjectDescription}
              onChange={set('crmProjectDescription')}
            />
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2.5 }} />

        {/* Location */}
        <SectionLabel>Location</SectionLabel>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Location Title / Drop Pin Name *"
              placeholder="e.g. Main Forest Entrance"
              value={form.dropPin}
              onChange={set('dropPin')}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <LocationField
              value={{ lat: form.dropPinLatitude, lng: form.dropPinLongitude }}
              onChange={handleLocationChange}
            />
          </Grid>
        </Grid>

        <Divider sx={{ mb: 2.5 }} />

        {/* Address — manual fallback */}
        <SectionLabel>Address</SectionLabel>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Address Line 1"
              value={form.crmAddressLine1}
              onChange={set('crmAddressLine1')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField fullWidth size="small" label="City" value={form.crmAddressCity} onChange={set('crmAddressCity')} />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Town / State"
              value={form.crmAddressTown}
              onChange={set('crmAddressTown')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Postcode / ZIP"
              value={form.crmAddressZip}
              onChange={set('crmAddressZip')}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              size="small"
              label="Country"
              value={form.crmAddressCountry}
              onChange={set('crmAddressCountry')}
            />
          </Grid>
        </Grid>
      </Box>

      {/* ── Footer ── */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          gap: 1.5,
          justifyContent: 'flex-end',
          flexShrink: 0,
          bgcolor: 'background.paper',
        }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!isValid || loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}>
          {loading ? 'Saving…' : 'Save Owner'}
        </Button>
      </Box>
    </Drawer>
  );
}

export default AddOwnerDrawer;
